/**
 * PostHog Analytics - Inert if REACT_APP_POSTHOG_KEY is empty/missing
 * 
 * Events:
 * - landing_view: User views landing page
 * - cta_click: User clicks a CTA (property: cta)
 * - plan_intent: User expresses interest in a plan (property: plan)
 * - signup_started: User starts signup
 * - signup_completed: User completes signup
 * - terminal_view: User views trading terminal
 * 
 * IMPORTANT: Never send email, password, or PII as event properties
 */

import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST;

// Only initialize if key is provided
let isInitialized = false;

export const initPostHog = () => {
  if (!POSTHOG_KEY || POSTHOG_KEY.trim() === '') {
    // Inert mode - no errors, just don't do anything
    console.log('[PostHog] No key configured, analytics disabled');
    return;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST || 'https://app.posthog.com',
      persistence: 'memory', // No cookies
      capture_pageview: false, // Manual pageviews on route change
      autocapture: false, // Only capture explicit events
      disable_session_recording: true,
      loaded: () => {
        isInitialized = true;
        console.log('[PostHog] Initialized');
      }
    });
  } catch (error) {
    console.warn('[PostHog] Failed to initialize:', error);
  }
};

// Track page view manually
export const trackPageView = (pageName) => {
  if (!isInitialized) return;
  
  try {
    posthog.capture('$pageview', { 
      $current_url: window.location.href,
      page_name: pageName 
    });
  } catch (error) {
    // Silently fail
  }
};

// Track specific events
export const trackEvent = (eventName, properties = {}) => {
  if (!isInitialized) return;
  
  // Sanitize - never send PII
  const sanitized = { ...properties };
  delete sanitized.email;
  delete sanitized.password;
  delete sanitized.name;
  delete sanitized.token;
  
  try {
    posthog.capture(eventName, sanitized);
  } catch (error) {
    // Silently fail
  }
};

// Predefined event helpers
export const analytics = {
  landingView: () => trackEvent('landing_view'),
  ctaClick: (cta) => trackEvent('cta_click', { cta }),
  planIntent: (plan) => trackEvent('plan_intent', { plan }),
  signupStarted: () => trackEvent('signup_started'),
  signupCompleted: () => trackEvent('signup_completed'),
  terminalView: () => trackEvent('terminal_view'),
};

export default posthog;
